import { useState } from "react";
import { Modal } from ".";
import { Calendar, X } from "lucide-react";

interface Currency {
  name: string;
  abbr: string;
  symbol: string;
  flag: string;
  country?: string;
  iso3?: string;
  iso2?: string;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  currencies: Currency[];
  onApply: (filters: {
    status: string;
    fromDate: string;
    toDate: string;
    fromCurrency: string;
    toCurrency: string;
  }) => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, onApply, currencies }) => {
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [fromCurrencySearch, setFromCurrencySearch] = useState("");
  const [toCurrencySearch, setToCurrencySearch] = useState("");
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [toDropdownOpen, setToDropdownOpen] = useState(false);

  const handleApplyFilters = () => {
    onApply({ status, fromDate, toDate, fromCurrency, toCurrency });
    onClose();
  };

  const handleReset = () => {
    setStatus("");
    setFromDate("");
    setToDate("");
    setFromCurrency("");
    setToCurrency("");
    setFromCurrencySearch("");
    setToCurrencySearch("");
    setFromDropdownOpen(false);
    setToDropdownOpen(false);
    onApply({ status: "", fromDate: "", toDate: "", fromCurrency: "", toCurrency: "" });
    onClose();
  };

  const statusOptions = ["ALL", "PENDING", "PROCESSING", "FAILED", "SUCCESSFUL", "CANCELLED"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md mx-auto" showCloseButton={false} transparency={0.5}>
      <div className="bg-white rounded-lg shadow-xl w-full dark:bg-gray-900">

        {/* Header */}
        <div className="flex justify-between items-center border-b p-4 dark:border-gray-700">
          <h2 className="text-xl font-semibold dark:text-white">Filter Exchanges</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Date Range */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="text-sm text-gray-500">From Date</label>
                <div
                  className="flex items-center border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 cursor-pointer"
                  onClick={() => (document.getElementById("allOrdersFromDate") as HTMLInputElement)?.showPicker()}
                >
                  <span className="pl-3 pr-2 text-gray-400 flex-shrink-0">
                    <Calendar size={16} />
                  </span>
                  <input
                    id="allOrdersFromDate"
                    type="date"
                    className="flex-1 py-2 pr-2 text-sm text-gray-700 dark:text-white bg-transparent outline-none border-none cursor-pointer"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500">To Date</label>
                <div
                  className="flex items-center border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 cursor-pointer"
                  onClick={() => (document.getElementById("allOrdersToDate") as HTMLInputElement)?.showPicker()}
                >
                  <span className="pl-3 pr-2 text-gray-400 flex-shrink-0">
                    <Calendar size={16} />
                  </span>
                  <input
                    id="allOrdersToDate"
                    type="date"
                    className="flex-1 py-2 pr-2 text-sm text-gray-700 dark:text-white bg-transparent outline-none border-none cursor-pointer"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Currency */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currency</h3>
            <div className="flex flex-col sm:flex-row gap-3">

              {/* From Currency */}
              <div className="flex-1">
                <label className="text-sm text-gray-500">From Currency</label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    onClick={() => { setFromDropdownOpen(!fromDropdownOpen); setToDropdownOpen(false); }}
                  >
                    {fromCurrency || "All currencies"}
                  </button>
                  {fromDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => { setFromDropdownOpen(false); setFromCurrencySearch(""); }}
                      />
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                        <div className="p-2 border-b dark:border-gray-700">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search currency..."
                            value={fromCurrencySearch}
                            onChange={(e) => setFromCurrencySearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-white"
                            onClick={() => { setFromCurrency(""); setFromDropdownOpen(false); setFromCurrencySearch(""); }}
                          >
                            All currencies
                          </button>
                          {(() => {
                            const search = fromCurrencySearch.trim().toLowerCase();
                            const filtered = currencies.filter((c) =>
                              !search ||
                              c.name.toLowerCase().includes(search) ||
                              c.abbr.toLowerCase().includes(search) ||
                              c.country?.toLowerCase().includes(search) ||
                              c.iso3?.toLowerCase().includes(search)
                            );
                            return filtered.length === 0
                              ? <p className="px-4 py-2 text-sm text-gray-400">No results</p>
                              : filtered.map((currency) => (
                                <button
                                  key={`from-${currency.abbr}-${currency.iso3}`}
                                  type="button"
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                  onClick={() => { setFromCurrency(currency.name); setFromDropdownOpen(false); setFromCurrencySearch(""); }}
                                >
                                  {currency.flag && (
                                    <div className="h-5 w-5 overflow-hidden rounded-full flex-shrink-0">
                                      <img src={currency.flag} alt={currency.abbr} className="h-full w-full object-cover" />
                                    </div>
                                  )}
                                  <span className="text-gray-700 dark:text-white">{currency.name}</span>
                                  <span className="ml-auto text-xs text-gray-400">{currency.abbr}</span>
                                </button>
                              ));
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* To Currency */}
              <div className="flex-1">
                <label className="text-sm text-gray-500">To Currency</label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-gray-700 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    onClick={() => { setToDropdownOpen(!toDropdownOpen); setFromDropdownOpen(false); }}
                  >
                    {toCurrency || "All currencies"}
                  </button>
                  {toDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => { setToDropdownOpen(false); setToCurrencySearch(""); }}
                      />
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                        <div className="p-2 border-b dark:border-gray-700">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search currency..."
                            value={toCurrencySearch}
                            onChange={(e) => setToCurrencySearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-white"
                            onClick={() => { setToCurrency(""); setToDropdownOpen(false); setToCurrencySearch(""); }}
                          >
                            All currencies
                          </button>
                          {(() => {
                            const search = toCurrencySearch.trim().toLowerCase();
                            const filtered = currencies.filter((c) =>
                              !search ||
                              c.name.toLowerCase().includes(search) ||
                              c.abbr.toLowerCase().includes(search) ||
                              c.country?.toLowerCase().includes(search) ||
                              c.iso3?.toLowerCase().includes(search)
                            );
                            return filtered.length === 0
                              ? <p className="px-4 py-2 text-sm text-gray-400">No results</p>
                              : filtered.map((currency) => (
                                <button
                                  key={`to-${currency.abbr}-${currency.iso3}`}
                                  type="button"
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                  onClick={() => { setToCurrency(currency.name); setToDropdownOpen(false); setToCurrencySearch(""); }}
                                >
                                  {currency.flag && (
                                    <div className="h-5 w-5 overflow-hidden rounded-full flex-shrink-0">
                                      <img src={currency.flag} alt={currency.abbr} className="h-full w-full object-cover" />
                                    </div>
                                  )}
                                  <span className="text-gray-700 dark:text-white">{currency.name}</span>
                                  <span className="ml-auto text-xs text-gray-400">{currency.abbr}</span>
                                </button>
                              ));
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Status Pills */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</h3>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s === "ALL" ? "" : s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    (s === "ALL" && status === "") || status.toUpperCase() === s
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="border-t dark:border-gray-700 p-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleReset}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Apply Filters
          </button>
        </div>

      </div>
    </Modal>
  );
};